<!-- ExperimentDetails.vue -->
<template>
  <div class="experiment-details" v-if="experiment">
    <div class="header">
      <div class="header-content">
        <h2>{{ isEditMode ? "Edit Experiment" : experiment.name }}</h2>
        <div class="top-buttons">
          <!-- Timeseries -->
          <button @click="$emit('openTimeseries')" class="icon-btn" title="Timeseries">
            <i class="bi bi-graph-up"></i>
          </button>
          <!-- Links toggle -->
          <button
            @click="toggleLinked"
            class="icon-btn"
            :title="showLinked ? 'Hide links' : 'Show links'"
          >
            <i class="bi bi-link-45deg"></i>
          </button>
          <button @click="toggleEditMode" class="icon-btn" title="Edit">
            <i class="bi bi-pencil"></i>
          </button>
          <button class="remove-btn" @click="deleteExperiment" title="Delete">
            <i class="bi bi-trash"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Graph connections zone -->
    <div v-if="showLinked">
      <!-- When adding connections -->
      <AddGraphConnection
        v-if="showAddLinks"
        :sourceNodeId="experiment.id"
        @cancel="showAddLinks = false"
        @connected="handleConnectionCreated"
      />
      <!-- When just listing connections -->
      <GraphPanel
        v-else
        :key="linkedPanelKey"
        :nodeId="experiment.id"
        @addRequested="showAddLinks = true"
        @closeRequested="showLinked = false"
      />
    </div>

    <!-- Display Mode -->
    <div v-else-if="!isEditMode">
      <div class="detail"><strong>Start Date:</strong> {{ formatDate(experiment.start_date) }}</div>
      <div class="detail"><strong>End Date:</strong> {{ formatDate(experiment.end_date) }}</div>
      <div class="detail"><strong>Duration:</strong> {{ displayDuration }}</div>
      <div class="detail"><strong>Location:</strong> {{ location.name }}</div>
      <div class="detail"><strong>Project:</strong> {{ project.name }}</div>
      <div class="detail"><strong>User:</strong> {{ user.name }}</div>

      <!-- Metadata (M2M) -->
      <section v-if="showMetadata" style="margin-top:16px">
        <NodeMdList
          :key="experiment.id"
          assType="experiment"
          :assId="experiment.id"
          @mdListUpdated="noop"
        />
      </section>
    </div>

    <!-- Edit Mode -->
    <div v-else>
      <div class="form-group">
        <label for="name">ID</label>
        <input type="text" id="name" v-model="editableExperiment.name">
      </div>
      <div class="form-group">
        <label for="start_date">Start Date</label>
        <input type="datetime-local" id="start_date" v-model="editableExperiment.start_date">
      </div>
      <div class="form-group">
        <label for="end_date">End Date</label>
        <input type="datetime-local" id="end_date" v-model="editableExperiment.end_date">
      </div>

      <div class="buttons-container">
        <button @click="saveChanges">Save</button>
        <button @click="cancelEditMode">Cancel</button>
      </div>
    </div>
  </div>
</template>

<script>
import { API_ENDPOINTS } from '@/services/config';
import apiService from '@/services/apiService';
import { useToast } from 'vue-toastification';
import NodeMdList from '@/components/NodeMdList.vue';
import GraphPanel from '@/components/GraphPanel.vue';
import AddGraphConnection from '@/components/AddGraphConnection.vue';
import { confirmDelete } from "@/utils/confirmDelete";
import { mapState } from 'vuex';

export default {
  components: { NodeMdList, GraphPanel, AddGraphConnection },
  props: {
    experiment: { type: Object, required: true, default: () => ({}) },
    showMetadata: { type: Boolean, default: true },
  },
  emits: ['updateExperiment', 'openTimeseries'],
  data() {
    return {
      isEditMode: false,
      editableExperiment: {},
      user: "", location: "", project: "",
      isExperimentRunning: false,
      showLinked: false,
      showAddLinks: false,
      linkedPanelKey: 0, // force remount/refresh after linking
    };
  },
  computed: {
    ...mapState(['loggedInUser']),
    displayDuration() {
      if (!this.experiment.start_date || !this.experiment.end_date) return 'N/A';
      const start = new Date(this.experiment.start_date);
      const end = new Date(this.experiment.end_date);
      const duration = end - start;
      const seconds = Math.floor((duration / 1000) % 60);
      const minutes = Math.floor((duration / 1000 / 60) % 60);
      const hours = Math.floor(duration / (1000 * 60 * 60));
      return `${hours} hour(s), ${minutes} minute(s), ${seconds} second(s)`;
    },
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  methods: {
    toggleLinked() {
      // toggling off should also exit add mode
      if (this.showLinked) this.showAddLinks = false;
      this.showLinked = !this.showLinked;
    },
    handleConnectionCreated() {
      // come back to the list view and refresh it
      this.showAddLinks = false;
      // bump key to remount GraphPanel so it refetches
      this.linkedPanelKey += 1;
    },

    noop() {},

    async fetchUserData() {
      try {
        const userResponse = await apiService.fetchResources(API_ENDPOINTS.user(this.experiment.user_id), this.loggedInUser.entra_id);
        this.user = userResponse;
      } catch (error) {
        this.toast.error("Failed to fetch user data!");
        console.error('Failed to fetch user data:', error);
      }
    },
    async fetchLocationData() {
      try {
        const locationResponse = await apiService.fetchResources(API_ENDPOINTS.location(this.experiment.location_id), this.loggedInUser.entra_id);
        this.location = locationResponse;
      } catch (error) {
        this.toast.error("Failed to fetch location data!");
        console.error('Failed to fetch location data:', error);
      }
    },
    async fetchProjectData() {
      try {
        const projectResponse = await apiService.fetchResources(API_ENDPOINTS.project(this.experiment.project_id), this.loggedInUser.entra_id);
        this.project = projectResponse;
      } catch (error) {
        this.toast.error("Failed to fetch project data!");
        console.error('Failed to fetch project data:', error);
      }
    },
    async saveChanges() {
      try {
        this.$emit('updateExperiment', { ...this.editableExperiment });
        this.isEditMode = false;
        this.determineRunningState();
      } catch (error) {
        this.toast.error("Error saving changes!");
        console.error('Error saving changes:', error);
      }
    },
    cancelEditMode() {
      this.isEditMode = false;
    },
    determineRunningState() {
      this.isExperimentRunning = !!this.experiment.start_date && !this.experiment.end_date;
    },
    toggleExperiment() {
      if (this.isExperimentRunning) {
        this.stopExperiment();
      } else {
        this.startExperiment();
      }
    },
    startExperiment() {
      this.editableExperiment = JSON.parse(JSON.stringify(this.experiment));
      this.editableExperiment.start_date = new Date().toISOString();
      this.editableExperiment.end_date = null;
      this.$emit('updateExperiment', this.editableExperiment);
      this.toast.success("Experiment started successfully!");
    },
    stopExperiment() {
      this.editableExperiment = JSON.parse(JSON.stringify(this.experiment));
      this.editableExperiment.end_date = new Date().toISOString();
      this.$emit('updateExperiment', this.editableExperiment);
      this.toast.success("Experiment stopped successfully!");
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const options = { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' };
      return new Date(dateString).toLocaleDateString(undefined, options);
    },
    toggleEditMode() {
      this.isEditMode = true;
      this.editableExperiment = JSON.parse(JSON.stringify(this.experiment));
    },
    async deleteExperiment() {
      try {
        const isConfirmed = await confirmDelete("Are you sure you want to delete this Experiment?");
        if (!isConfirmed) return;
        await apiService.deleteResource(API_ENDPOINTS.experiment(this.experiment.id), this.loggedInUser.id, this.loggedInUser.entra_id);
        if (window.history.length > 1) {
          this.$router.back();
        } else {
          this.$router.push({ name: "Experiments" });
        }
        this.toast.success("Experiment deleted successfully!");
      } catch (error) {
        this.toast.error("Failed to delete experiment!");
        console.error('Error deleting experiment:', error);
      }
    },
  },
  watch: {
    experiment: {
      handler() {
        this.determineRunningState();
        this.fetchProjectData();
        this.fetchLocationData();
        this.fetchUserData();
      },
      immediate: true,
      deep: true,
    },
    // If route id changes, close linked panel and exit add mode
    '$route.params.id'() {
      this.showLinked = false;
      this.showAddLinks = false;
    },
  },
  mounted() {
    this.fetchProjectData();
    this.fetchLocationData();
    this.fetchUserData();
    this.determineRunningState();
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/main.scss";

.experiment-details {
  max-width: 800px;
  margin: 0 auto;
  padding: 15px 20px;
  border-radius: 8px;
  background-color: $secondary-color;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

  .header {
    display: flex;
    flex-direction: column;
    gap: 5px;

    .header-content {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      width: 100%;

      h2 {
        margin: 0;
        flex: 1;
        min-width: 0;
        overflow-wrap: anywhere;
      }
    }

    .experiment-path {
      font-size: 1rem;
    }

    .top-buttons {
      display: flex;
      gap: 10px;
      flex-shrink: 0;
      margin-left: 10px;
    }
  }

  .detail {
    margin-bottom: 3px;
  }

  .detail ul {
    list-style-type: none;
    padding: 0;
    margin-top: 0;
    margin-bottom: 3px;
    li { padding: 3px 0; }
  }

  .buttons-container {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
  }
}
</style>
